import Box from "@mui/material/Box";
import type { SxProps, Theme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { useRouterState } from "@tanstack/react-router";
import { type ReactNode, useRef } from "react";

import { StyledAnchor } from "@/components/common/styles";
import { newTabProps } from "@/components/marketing/marketingLinks";
import MarketingLottie from "@/components/marketing/MarketingLottie";
import {
  BODY,
  BODY_SMALL,
  HEADING_2,
} from "@/components/marketing/marketingType";
import {
  ACCENT,
  ACCENT_DEEP,
  MID,
  NOT_WIDE,
  TINY,
  marketingPalette,
} from "@/components/marketing/tokens";
import type { Laid } from "@/content/landing/sectionLayout";
import type { LandingBlock } from "@/content/landing/types";
import { useNeighbours } from "../LandingPageContext";
import {
  LandingButton,
  LandingImage,
  LandingSection,
} from "../LandingPrimitives";
import { TRUSTPILOT_BUSINESS_UNIT, useTrustpilotWidget } from "../trustpilot";
import { PlainBandHeading, SHALLOW_FOOT } from "./landingBands";
import {
  CONTAIN_GAP,
  continuesRun,
  FLOW_MEASURE,
  flowSection,
  RUN_GAP,
  sectionFoot,
  sectionPad,
} from "./landingRun";
import { Lines } from "@/components/marketing/Lines";
import { OUT_QUAD } from "@/components/marketing/easing";

export { CheckListBlock } from "./CheckListBlock";
export { CtaBandBlock } from "./CtaBandBlock";

type Heading = Laid<"heading">;
type Tabs = Laid<"tabs">;
type Prose = Laid<"prose">;

/** `testimonial_wrap-trust-pilot`: the mint band six of these pages close on. */
const TRUSTPILOT = {
  lottie: "/marketing/home/love-hearts.json",
  height: 240,
  href: "https://uk.trustpilot.com/review/webshare.io",
  widget: {
    "data-locale": "en-GB",
    "data-template-id": "54ad5defc6454f065c28af8b",
    "data-businessunit-id": TRUSTPILOT_BUSINESS_UNIT,
    "data-style-width": "100%",
    "data-theme": "light",
    "data-stars": "5",
    "data-review-languages": "en",
  },
} as const;

/**
 * Hidden until the panel is hovered, which is what the published page does:
 * an interaction that fades the hearts to 0.95 over half a second, and back
 * out over 400.
 */
const HEART_CLASS = "landing-hearts";
const HEART_REVEAL = {
  opacity: 0,
  transition: `opacity 400ms ${OUT_QUAD}`,
  "@media (prefers-reduced-motion: reduce)": { transition: "none" },
} as const;

/**
 * `.hearts_lottie`: a square as tall as the band it is drawn in, which is 432
 * where the band states its own height and 487 where a line under the heading
 * makes it deeper. Taken from the box rather than restated per step.
 */
const HEART = { aspectRatio: "1 / 1" };

const TRUST_BAND = {
  position: "relative",
  display: "flex",
  flexDirection: "column",
  gap: "32px",
  width: 1,
  p: "64px 64px 48px",
  borderRadius: "32px",
  backgroundColor: marketingPalette.trustPanel,
  overflow: "hidden",
  [`&:hover .${HEART_CLASS}`]: {
    opacity: 0.95,
    transition: `opacity 500ms ${OUT_QUAD}`,
  },
  [NOT_WIDE]: { p: "32px 32px 16px" },
  [MID]: { p: "16px" },
  [TINY]: { gap: "16px", borderRadius: "16px" },
};

/**
 * `.alter-benefit_list`: on the comparison pages the band is one step of a
 * narrower column, not a section of its own. Those are the pages that publish
 * a line under the heading, and the only ones that do.
 */
const TRUST_RAIL = { maxWidth: "1088px", mx: "auto" };

/**
 * `.reliable-title_wrap`: heading and copy in one 592 column, not across the
 * band. The same wrap opens every published heading block.
 */
const TITLE_WRAP = { width: 1, maxWidth: "592px", mx: "auto" };

/**
 * `.pr-c-title_wrap`: the title of the use-case row under it, published as
 * one section with the row. 70% of the container, then all of it.
 */
const ROW_TITLE_WRAP = { width: "70%", mx: "auto", [NOT_WIDE]: { width: 1 } };

/** That title sits 40 over the row's cards, closing to 24 on a phone. */
const ROW_TITLE_GAP = { pb: "40px", [TINY]: { pb: "24px" } };

/**
 * `.testimonial_wrap-trust-pilot`: the green band, its heading, and the hearts
 * that fade in under the pointer; the Trustpilot widget in it, or the band's
 * own reviews.
 */
export function TrustFrame({
  title,
  body,
  children,
}: {
  title: string;
  body?: string;
  children: ReactNode;
}) {
  return (
    <Box sx={[TRUST_BAND, body ? TRUST_RAIL : {}]}>
      {body ? (
        <PlainBandHeading title={title} body={body} sx={TITLE_WRAP} />
      ) : (
        // `.h2_new.is-centered-txt`: with no line under it the heading takes
        // the band's own width rather than a measure of its own.
        <Typography component="h2" sx={[HEADING_2, { textAlign: "center" }]}>
          <Lines text={title} />
        </Typography>
      )}
      {[{ inset: "0 -11% 0 auto" }, { inset: "0 auto 0 -12%" }].map((side) => (
        <Box
          key={side.inset}
          aria-hidden
          className={HEART_CLASS}
          sx={{ position: "absolute", ...side, ...HEART, ...HEART_REVEAL }}
        >
          <MarketingLottie
            path={TRUSTPILOT.lottie}
            loop
            play
            style={{ width: "100%", height: "100%", pointerEvents: "none" }}
          />
        </Box>
      ))}
      {children}
    </Box>
  );
}

function TrustBand({ title, body }: { title: string; body?: string }) {
  const widget = useRef<HTMLDivElement>(null);
  useTrustpilotWidget(widget);

  return (
    <TrustFrame title={title} body={body}>
      {/* The height is held here as well as passed to the widget: the bootstrap
          replaces this element's contents, and without a box to replace into,
          the band collapses and then jumps. */}
      <Box
        ref={widget}
        className="trustpilot-widget"
        data-style-height={`${TRUSTPILOT.height}px`}
        {...TRUSTPILOT.widget}
        sx={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 1,
          height: `${TRUSTPILOT.height}px`,
        }}
      >
        <StyledAnchor
          href={TRUSTPILOT.href}
          target="_blank"
          rel="noopener"
          sx={{ color: marketingPalette.ink, fontSize: 16 }}
        >
          Read our reviews on Trustpilot
        </StyledAnchor>
      </Box>
    </TrustFrame>
  );
}

/**
 * What the trust band closes on: the run's gutter where it is a step of one,
 * `.container_new.padding-bottom` where a section follows it, and the full
 * break where it closes the page.
 */
function trustFoot(next: LandingBlock | undefined) {
  if (continuesRun(next)) {
    return RUN_GAP;
  }
  return next === undefined ? undefined : SHALLOW_FOOT;
}

/**
 * What a heading closes on: the run's gutter where it opens a run, the row's
 * own step where it titles the use-case row under it, the break otherwise.
 */
function headingFoot(next: LandingBlock | undefined) {
  if (next?.kind === "useCaseRow" && !next.title) {
    return ROW_TITLE_GAP;
  }
  return continuesRun(next) ? RUN_GAP : undefined;
}

/**
 * The trust band, and the plain centred heading rows beside it. The published
 * pages set each heading row as one section with what follows it, so the row
 * closes on that section's own step rather than a break of its own.
 */
export function HeadingBlock({ block }: { block: Heading }) {
  const { next } = useNeighbours(block);
  if (block.trustStrip) {
    return (
      <LandingSection
        {...(block.pad
          ? { padding: sectionPad(block.pad) }
          : sectionFoot(trustFoot(next)))}
        // One page publishes the band on the grey section its questions sit in.
        ground={block.ground === "surface" ? "grey" : undefined}
      >
        <TrustBand title={block.title} body={block.body} />
      </LandingSection>
    );
  }
  const titlesRow = next?.kind === "useCaseRow" && !next.title;
  return (
    <LandingSection {...sectionFoot(headingFoot(next))}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
        <PlainBandHeading
          title={block.title}
          body={block.body}
          bodyStyle={block.bodyTone === "small" ? BODY_SMALL : BODY}
          sx={titlesRow ? ROW_TITLE_WRAP : TITLE_WRAP}
        />
        {block.ctas?.length ? (
          <Box sx={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
            {block.ctas.map((cta, index) => (
              <LandingButton key={index} cta={cta} />
            ))}
          </Box>
        ) : null}
      </Box>
    </LandingSection>
  );
}

/** `.aff-ref-tab_list`: the grey trough the two pills sit in. */
const TAB_TRACK = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "16px",
  width: "fit-content",
  mx: "auto",
  p: "8px",
  borderRadius: "16px",
  // The published trough's own grey; no palette role carries it.
  backgroundColor: "#F0F3F5",
  [MID]: { width: 1 },
};

/**
 * `.aff-ref-tab_item`: a white rule on a white pill, so the selected state's
 * accent rule arrives without moving anything.
 */
const TAB_ITEM = {
  display: "flex",
  gap: "16px",
  width: "276.8px",
  p: "8px 10.08px",
  borderRadius: "12px",
  border: "1px solid #FFFFFF",
  backgroundColor: "common.white",
  color: marketingPalette.ink,
  fontSize: 15.04,
  lineHeight: "120%",
  fontWeight: 500,
  "&:hover": { borderColor: marketingPalette.inkSubtle },
  [MID]: {
    width: 1,
    alignItems: "center",
    justifyContent: "center",
    p: "6.4px",
  },
};

/**
 * The pill for the page you are on. Its rule is restated under the pointer:
 * the published selected pill does not take the resting pill's hover, and a
 * bare `borderColor` would lose to `TAB_ITEM`'s own `:hover`.
 */
const TAB_ITEM_CURRENT = {
  borderColor: ACCENT,
  color: ACCENT_DEEP,
  "&:hover": { borderColor: ACCENT },
};

const TAB_ITEM_BODY = {
  color: "rgba(79, 92, 101, 0.9)",
  fontSize: 12.96,
  fontWeight: 400,
  [MID]: { display: "none" },
};

const TAB_ITEM_ICON = {
  width: "38.08px",
  height: "38.08px",
  flexShrink: 0,
  [TINY]: { width: "32px", height: "32px" },
};

/**
 * `.aff-ref-tab_wrap`: the switcher opens 72 down the page (4.5rem of top
 * padding) and 48 from 767 down, whatever the nav's height: a pixel under this
 * app's 73 header at the wide step, 18 past its 54 one below it, and six under
 * that from 767 down, where the trough shows beneath the nav.
 */
const TAB_SECTION = {
  mt: "-1px",
  [NOT_WIDE]: { mt: "18px" },
  [MID]: { mt: "-6px" },
};

/**
 * The referral/affiliate switcher above both pages' heroes. No padding: it
 * opens the page under the nav rather than starting a section of its own.
 */
export function TabsBlock({ block }: { block: Tabs }) {
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });
  return (
    <LandingSection padding="none" sx={TAB_SECTION}>
      <Box sx={TAB_TRACK}>
        {block.items.map((item, index) => {
          const current =
            item.href !== undefined && samePath(item.href, pathname);
          return (
            <StyledAnchor
              key={index}
              href={item.href ?? "#"}
              {...newTabProps(item)}
              aria-current={current ? "page" : undefined}
              sx={[TAB_ITEM, current ? TAB_ITEM_CURRENT : null]}
            >
              {item.icon ? (
                <LandingImage src={item.icon} sx={TAB_ITEM_ICON} />
              ) : null}
              <Box>
                <Box>{item.title}</Box>
                {item.body ? <Box sx={TAB_ITEM_BODY}>{item.body}</Box> : null}
              </Box>
            </StyledAnchor>
          );
        })}
      </Box>
    </LandingSection>
  );
}

/** Both spellings of the same landing path; the router keeps the trailing slash. */
function samePath(a: string, b: string): boolean {
  const trim = (path: string) => path.replace(/\/+$/, "");
  return trim(a) === trim(b);
}

const PROSE_HEADING = { h2: "h2", h3: "h3", h4: "h4" } as const;

/**
 * `.eth-table-title_wrap`: the heading centred over its line, 16 apart. The
 * `intro` shape holds the line inside the wrap at the small size; the
 * `titleBody` shape sets it under the wrap at body size, full width and left.
 */
const PROSE_TITLE_WRAP = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  textAlign: "center",
  gap: "16px",
  width: 1,
} satisfies SxProps<Theme>;

/** A prose block in the ethics page's flowing column: a heading wrap, then whatever follows it. */
function ContainedProse({ block }: { block: Prose }) {
  const intro = block.proseShape === "intro";
  const [head, ...rest] = block.parts;
  const wrapped = intro ? block.parts : head ? [head] : [];
  const under = intro ? [] : rest;
  return (
    <LandingSection {...flowSection(block)}>
      <Box
        sx={[
          { display: "flex", flexDirection: "column" },
          FLOW_MEASURE,
          CONTAIN_GAP,
        ]}
      >
        <Box sx={PROSE_TITLE_WRAP}>
          {wrapped.map((part, index) =>
            part.tag === "p" || part.tag === "li" ? (
              <Typography key={index} sx={BODY_SMALL}>
                {part.text}
              </Typography>
            ) : (
              <Typography
                key={index}
                component={PROSE_HEADING[part.tag]}
                sx={HEADING_2}
              >
                <Lines text={part.text} />
              </Typography>
            )
          )}
        </Box>
        {under.map((part, index) => (
          <Typography key={index} sx={BODY}>
            {part.text}
          </Typography>
        ))}
      </Box>
    </LandingSection>
  );
}

/** The ethics page, which is flowing copy rather than sections. */
export function ProseBlock({ block }: { block: Prose }) {
  if (block.contain) {
    return <ContainedProse block={block} />;
  }
  return (
    <LandingSection padding="bottom">
      <Box
        sx={{
          maxWidth: "820px",
          display: "flex",
          flexDirection: "column",
          gap: "16px",
        }}
      >
        {block.parts.map((part, index) =>
          part.tag === "p" || part.tag === "li" ? (
            <Typography
              key={`${part.tag}-${index}`}
              component={part.tag === "li" ? "li" : "p"}
              sx={[BODY_SMALL, part.tag === "li" ? { ml: "20px" } : {}]}
            >
              {part.text}
            </Typography>
          ) : (
            <Typography
              key={`${part.tag}-${index}`}
              component={PROSE_HEADING[part.tag]}
              sx={[
                HEADING_2,
                part.tag === "h3" ? { fontSize: 28 } : {},
                part.tag === "h4" ? { fontSize: 20 } : {},
                { mt: "16px" },
              ]}
            >
              {part.text}
            </Typography>
          )
        )}
      </Box>
    </LandingSection>
  );
}
